import { useEffect, useState } from "react";
import api, { fileUrl } from "../lib/api";
import { ATTACH_CATEGORIES } from "../lib/constants";
import { Button } from "./ui/button";
import { Dialog, DialogContent } from "./ui/dialog";
import { toast } from "sonner";
import { Camera, Upload, Trash2, FileText, X, Image as ImageIcon } from "lucide-react";

function AuthImage({ attId, alt, className, onClick }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url;
    let active = true;
    api.get(`/attachments/${attId}/download`, { responseType: "blob" })
      .then((r) => { if (active) { url = URL.createObjectURL(r.data); setSrc(url); } })
      .catch(() => {});
    return () => { active = false; if (url) URL.revokeObjectURL(url); };
  }, [attId]);
  if (!src) return <div className={`bg-muted animate-pulse ${className}`} />;
  return <img src={src} alt={alt} className={className} onClick={onClick} />;
}

export default function AttachmentsPanel({ appointmentId, clientId, jobId, quoteId, canUpload = true, compact = false, title = "Foto e allegati" }) {
  const [items, setItems] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [category, setCategory] = useState("foto_sopralluogo");
  const [lightbox, setLightbox] = useState(null);

  const query = () => {
    const p = new URLSearchParams();
    if (appointmentId) p.set("appointment_id", appointmentId);
    else if (jobId) p.set("job_id", jobId);
    else if (quoteId) p.set("quote_id", quoteId);
    else if (clientId) p.set("client_id", clientId);
    return p.toString();
  };

  const load = async () => {
    const { data } = await api.get(`/attachments?${query()}`);
    setItems(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [appointmentId, clientId, jobId, quoteId]);

  const upload = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("category", category);
        if (appointmentId) fd.append("appointment_id", appointmentId);
        if (clientId) fd.append("client_id", clientId);
        if (jobId) fd.append("job_id", jobId);
        if (quoteId) fd.append("quote_id", quoteId);
        await api.post("/attachments", fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success("Caricamento riuscito ✓");
      await load();
    } catch (e) {
      toast.error("Errore nel caricamento");
    } finally {
      setUploading(false);
    }
  };

  const remove = async (id) => {
    await api.delete(`/attachments/${id}`);
    toast.success("Allegato rimosso");
    load();
  };

  const isImage = (ct) => (ct || "").startsWith("image/");

  return (
    <div data-testid="attachments-panel">
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-head font-semibold flex items-center gap-2"><ImageIcon size={18} className="text-brand" /> {title} <span className="text-xs text-muted-foreground">({items.length})</span></h4>
      </div>

      {canUpload && (
        <div className="card-soft p-3 mb-4">
          <div className="flex flex-wrap gap-1.5 mb-3">
            {ATTACH_CATEGORIES.map((c) => (
              <button key={c.value} data-testid={`cat-${c.value}`} onClick={() => setCategory(c.value)}
                className={`text-xs px-2.5 py-1.5 rounded-full border transition ${category === c.value ? "bg-brand text-white border-brand" : "bg-white text-muted-foreground border-border"}`}>
                {c.label}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap gap-2">
            <label data-testid="btn-add-photo" className="flex-1 min-w-[140px] cursor-pointer">
              <div className="flex items-center justify-center gap-2 bg-brand text-white rounded-lg py-2.5 text-sm font-medium hover:bg-[#A8472F] transition">
                <Camera size={18} /> Aggiungi foto o foglio
              </div>
              <input type="file" accept="image/*" capture="environment" multiple className="hidden"
                onChange={(e) => upload(Array.from(e.target.files))} />
            </label>
            <label data-testid="btn-upload-file" className="flex-1 min-w-[140px] cursor-pointer">
              <div className="flex items-center justify-center gap-2 bg-ink text-white rounded-lg py-2.5 text-sm font-medium hover:opacity-90 transition">
                <Upload size={18} /> Galleria / File
              </div>
              <input type="file" accept="image/*,application/pdf" multiple className="hidden"
                onChange={(e) => upload(Array.from(e.target.files))} />
            </label>
          </div>
          {uploading && <p className="text-xs text-muted-foreground mt-2 animate-pulse">Caricamento in corso…</p>}
        </div>
      )}

      {items.length === 0 ? (
        <p className="text-sm text-muted-foreground py-4 text-center">Nessun allegato ancora.</p>
      ) : (
        <div className={`grid gap-3 ${compact ? "grid-cols-3 sm:grid-cols-4" : "grid-cols-2 sm:grid-cols-3 md:grid-cols-4"}`}>
          {items.map((a) => (
            <div key={a.id} data-testid={`attachment-${a.id}`} className="card-soft overflow-hidden group relative">
              <div className="aspect-square bg-muted flex items-center justify-center cursor-pointer" onClick={() => isImage(a.content_type) && setLightbox(a)}>
                {isImage(a.content_type)
                  ? <AuthImage attId={a.id} alt={a.original_filename} className="w-full h-full object-cover" />
                  : <a href={fileUrl(a.id)} target="_blank" rel="noreferrer" className="flex flex-col items-center text-muted-foreground p-2"><FileText size={32} /><span className="text-[10px] mt-1 text-center line-clamp-2">{a.original_filename}</span></a>}
              </div>
              <div className="p-2">
                <p className="text-[10px] font-medium text-brand uppercase tracking-wide truncate">{ATTACH_CATEGORIES.find(c=>c.value===a.category)?.label || a.category}</p>
                {a.description && <p className="text-[11px] text-muted-foreground truncate">{a.description}</p>}
                <p className="text-[10px] text-muted-foreground mt-0.5">{a.uploaded_by} · {new Date(a.created_at).toLocaleDateString("it-IT")}</p>
              </div>
              {canUpload && (
                <button data-testid={`del-attachment-${a.id}`} onClick={() => remove(a.id)}
                  className="absolute top-1.5 right-1.5 bg-white/90 rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition shadow">
                  <Trash2 size={14} className="text-destructive" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!lightbox} onOpenChange={() => setLightbox(null)}>
        <DialogContent className="max-w-3xl p-2">
          {lightbox && (
            <div>
              <AuthImage attId={lightbox.id} alt={lightbox.original_filename} className="w-full max-h-[75vh] object-contain rounded" />
              <p className="text-sm text-center mt-2 text-muted-foreground">{lightbox.description || lightbox.original_filename}</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
